import { defaultMergePatch } from "@platform/hooks";
import isPlainObject from "lodash/isPlainObject";

import type {
  FinancialsTableResponse,
  PatchFinancialsTableRequest,
} from "@platform/api";

type Merge = (state: unknown, diff: unknown) => unknown;
type WithCarriers = { carriers?: readonly { id: string }[] };

/**
 * `merge` over everything but `carriers`, which merges by carrier id rather
 * than replacing the array: a cell edit names only its own carrier (see
 * `buildPatchDiff`), and replacing the array would drop every other one.
 */
const mergeWithCarriersById = <T extends WithCarriers>(
  merge: Merge,
  state: T | undefined,
  diff: T,
): T => {
  const { carriers: diffCarriers, ...rest } = diff;
  const merged = merge(state, rest) as T;
  if (diffCarriers === undefined) return merged;

  const existing = state?.carriers ?? [];
  const incoming = new Map(
    diffCarriers.map((carrier) => [carrier.id, carrier]),
  );
  const known = new Set(existing.map((carrier) => carrier.id));

  return {
    ...merged,
    carriers: [
      ...existing.map((carrier) =>
        incoming.has(carrier.id)
          ? merge(carrier, incoming.get(carrier.id))
          : carrier,
      ),
      ...diffCarriers.filter((carrier) => !known.has(carrier.id)),
    ],
  };
};

type CellEdit = {
  annualPremium?: number | null;
  note?: string | null;
  premiumOverridden?: true | null;
};

// A premium typed into a cell is an override from then on, and a cleared one
// isn't: say so in the cell, the way the server will on the next read.
const markOverride = (edit: CellEdit): CellEdit =>
  edit.annualPremium === undefined
    ? edit
    : {
        ...edit,
        premiumOverridden: edit.annualPremium === null ? null : true,
      };

const markCells = (
  coverages: Record<string, CellEdit>,
): Record<string, CellEdit> =>
  Object.fromEntries(
    Object.entries(coverages).map(([id, edit]) => [id, markOverride(edit)]),
  );

// A column diff without cells (a visibility toggle) must stay without them:
// an explicit `coverages: undefined` would merge over, and wipe, the cached
// cells.
const markColumnCells = <T extends { coverages?: Record<string, CellEdit> }>(
  column: T,
): T =>
  column.coverages === undefined
    ? column
    : { ...column, coverages: markCells(column.coverages) };

const markOverrides = (
  diff: PatchFinancialsTableRequest,
): PatchFinancialsTableRequest => ({
  ...diff,
  ...(diff.periods && {
    periods: Object.fromEntries(
      Object.entries(diff.periods).map(([period, column]) => [
        period,
        column && markColumnCells(column),
      ]),
    ),
  }),
  ...(diff.carriers && {
    carriers: diff.carriers.map(markColumnCells),
  }),
});

/**
 * Applies an edit to the cached table: JSON Merge Patch, carriers merged by
 * id, and each edited premium's override flag set to match. A cleared premium
 * loses its value and its flag; the calculated premium it falls back to comes
 * with the refetch a clear triggers (see `revertPremium`).
 */
export const applyFinancialsDiff = (
  state: FinancialsTableResponse,
  diff: PatchFinancialsTableRequest,
): FinancialsTableResponse =>
  mergeWithCarriersById(
    defaultMergePatch,
    state as WithCarriers,
    markOverrides(diff) as WithCarriers,
  ) as FinancialsTableResponse;

// A deep merge in which a later value wins, `null` included.
const mergeKeepingNulls: Merge = (state, diff) => {
  if (!isPlainObject(diff) || !isPlainObject(state)) return diff;

  const result: Record<string, unknown> = { ...(state as object) };
  for (const [key, value] of Object.entries(diff as object)) {
    result[key] = mergeKeepingNulls(result[key], value);
  }

  return result;
};

/**
 * Coalesces queued edits into one request body. Unlike the cache, a body
 * keeps a `null`: it is what tells the server to clear a value, so an edit
 * cleared again before the save goes out must still clear it.
 */
export const mergePendingFinancialsChanges = (
  existing: PatchFinancialsTableRequest | undefined,
  incoming: PatchFinancialsTableRequest,
): PatchFinancialsTableRequest =>
  mergeWithCarriersById(
    mergeKeepingNulls,
    existing as WithCarriers | undefined,
    incoming as WithCarriers,
  ) as PatchFinancialsTableRequest;
